import '../styles/TestDetails.scss';

import { InputField } from '@whistler-ui/k2/components/InputField';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Paragraph } from '@whistler-ui/k2/components/Typography';
import {
  formatDuration,
  formatIntlLongDateTime,
} from '@whistler-ui/k2/helpers';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { USE_CASE } from '../../../constants';
import {
  ExecutionTest,
  TStatuses,
  TTestTabs,
  TUseCaseType,
} from '../../../types';
import { componentName } from '../constants';
import { AssertionList } from './AssertionList';
import { TestLoader } from './TestLoader';

interface Props {
  test: any;
  activeContent: TTestTabs;
  execution: ExecutionTest;
  type: TUseCaseType;
  status: TStatuses;
  isLoading: boolean;
}

export const OverviewSection = (props: Props) => {
  const { test, activeContent, execution, type, status, isLoading } = props;
  const randomId = Math.floor(Math.random() * 1000);
  const sectionId = `${componentName}-${activeContent}-${randomId}`;
  const c = useClassHelper(componentName);

  const { t } = useTranslation();

  const assertions = useMemo(() => {
    if (type === USE_CASE && Array.isArray(execution?.api_test_executions)) {
      // Create an assertions map from each invocation
      return JSON.stringify(
        execution?.api_test_executions.reduce((assertions, { invocations }) => {
          const assertInvocation =
            Array.isArray(invocations) && invocations.length > 0
              ? JSON.parse(invocations?.[0]?.assertions || '{}')
              : {};

          return {
            ...assertions,
            ...assertInvocation,
          };
        }, {}),
      );
    } else {
      return execution?.invocations?.[0]?.assertions || '{}';
    }
  }, [type, execution]) as string;

  const executionTotalTime = useMemo(() => {
    if (type === USE_CASE && Array.isArray(execution?.api_test_executions)) {
      return execution.api_test_executions.reduce((time, exec) => {
        time += exec.total_response_time_ms;
        return time;
      }, 0);
    }

    return execution?.total_response_time_ms ?? 0;
  }, [type, execution]);

  if (isLoading) {
    return <TestLoader status={status} />;
  }

  const totalTime = formatDuration(executionTotalTime, {
    showAsAPlanDecimals: true,
  });

  return (
    <section id={sectionId} key={sectionId}>
      <Stack direction="column" gap={4}>
        <div className={c('__title')}>
          <Paragraph>
            {test.title} - {test.tag_line}
          </Paragraph>
        </div>
        {execution && Object.keys(execution).length > 0 ? (
          <Stack
            direction="column"
            gap={4}
            className={c('__overview-content-wrapper')}
          >
            <div className={c('__rows-details')}>
              <div className={c('__label')}>
                <Paragraph>{t('testDetails_test_description')}</Paragraph>
              </div>
              <div className={c('__label-details')}>
                <Paragraph>{t(test.description)}</Paragraph>
              </div>
            </div>
            <div className={c('__rows-details')}>
              <div className={c('__label')}>
                <Paragraph>{t('testDetails_total_response_time')}</Paragraph>
              </div>
              <div className={c('__label-details')}>
                <Paragraph>{totalTime ? `${totalTime} ms` : '-'}</Paragraph>
              </div>
            </div>
            <div className={c('__rows-details')}>
              <div className={c('__label')}>
                <Paragraph>{t('testDetails_invocation_timestamp')}</Paragraph>
              </div>
              <div className={c('__label-details')}>
                <Paragraph>
                  {formatIntlLongDateTime(execution?.timestamp)}
                </Paragraph>
              </div>
            </div>
            <div className={c('__rows-details')}>
              <div className={c('__label')}>
                <Paragraph>{t('testDetails_assertions')}</Paragraph>
              </div>
              <AssertionList
                assertions={assertions}
                status={execution.status}
              />
            </div>
            {execution?.error && (
              <div className={c('__rows-details')}>
                <div className={c('__label')}>
                  <Paragraph variant="error">
                    {t('testDetails_error')}
                  </Paragraph>
                </div>
                <div className={c('__label-details')}>
                  <InputField.ErrorMessage
                    error={execution.error}
                    errorMessage={
                      execution.error?.error_message ||
                      t('common:errors:default')
                    }
                    dataCyError="test-execution-error"
                  />
                </div>
              </div>
            )}
          </Stack>
        ) : (
          <Stack
            direction="column"
            gap={4}
            className={c('__overview-content-wrapper')}
          >
            <div className={c('__rows-details')}>
              <div className={c('__label')}>
                <Paragraph>{t('testDetails_test_description')}</Paragraph>
              </div>
              <div className={c('__label-details')}>
                <Paragraph>{test.description}</Paragraph>
              </div>
            </div>

            <div className={c('__rows-details')}>
              <div className={c('__label')}>
                <Paragraph>{t('testDetails_status')}</Paragraph>
              </div>
              <div className={c('__label-details')}>
                <Paragraph>{t('testDetails_test_not_executed')}</Paragraph>
              </div>
            </div>
          </Stack>
        )}
      </Stack>
    </section>
  );
};
